import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useUser, useClerk } from '@clerk/clerk-react';
import { useUserData } from '../contexts/UserContext';
import {
  BarChart3, Crown, Zap, Star, Plus, Trash2, Settings, LogOut,
  TrendingUp, Calendar, Clock, AlertCircle, ChevronRight, RefreshCw,
  MessageSquare, Rocket, Target, BookOpen, Search
} from 'lucide-react';

interface WatchlistItem {
  id: number;
  ticker: string;
  notes: string;
  added_at: string;
}

interface ScanHistory {
  ticker: string;
  score: number;
  status: string;
  scanned_at: string;
}

export const DashboardPage: React.FC = () => {
  const { user } = useUser();
  const { signOut } = useClerk();
  const { userData, loading, refreshUser } = useUserData();
  const navigate = useNavigate();

  const [watchlist, setWatchlist] = useState<WatchlistItem[]>([]);
  const [newTicker, setNewTicker] = useState('');
  const [loadingWatchlist, setLoadingWatchlist] = useState(true);
  const [scanHistory, setScanHistory] = useState<ScanHistory[]>([]);
  const [promoCode, setPromoCode] = useState('');
  const [promoMessage, setPromoMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const { applyPromoCode } = useUserData();

  // Fetch watchlist
  useEffect(() => {
    if (!user) return;

    const fetchWatchlist = async () => {
      try {
        const res = await fetch(`/api/watchlist?action=list&clerkId=${user.id}`);
        const data = await res.json();
        setWatchlist(data.watchlist || []);
      } catch (err) {
        console.error('Failed to fetch watchlist:', err);
      } finally {
        setLoadingWatchlist(false);
      }
    };

    fetchWatchlist();
  }, [user]);

  const handleAddToWatchlist = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newTicker.trim() || !user) return;

    try {
      const res = await fetch('/api/watchlist?action=add', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          clerkId: user.id,
          ticker: newTicker.toUpperCase(),
        }),
      });

      const data = await res.json();
      if (data.success) {
        setWatchlist([data.item, ...watchlist]);
        setNewTicker('');
      }
    } catch (err) {
      console.error('Failed to add to watchlist:', err);
    }
  };

  const handleRemoveFromWatchlist = async (ticker: string) => {
    if (!user) return;

    try {
      await fetch('/api/watchlist?action=remove', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          clerkId: user.id,
          ticker,
        }),
      });

      setWatchlist(watchlist.filter(item => item.ticker !== ticker));
    } catch (err) {
      console.error('Failed to remove from watchlist:', err);
    }
  };

  const handlePromoSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!promoCode.trim()) return;

    const result = await applyPromoCode(promoCode);
    setPromoMessage({
      type: result.success ? 'success' : 'error',
      text: result.message,
    });

    if (result.success) {
      setPromoCode('');
    }
  };

  const handleCancelSubscription = async () => {
    if (!user || !confirm('Are you sure you want to cancel your subscription?')) return;

    try {
      const res = await fetch('/api/subscription?action=cancel', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ clerkId: user.id }),
      });

      const data = await res.json();
      if (data.success) {
        await refreshUser();
        alert('Subscription cancelled. You will have access until the end of your billing period.');
      }
    } catch (err) {
      console.error('Failed to cancel subscription:', err);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-terminal-bg flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 text-cyan-400 animate-spin mx-auto mb-4" />
          <p className="text-terminal-dim">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  const tierConfig = {
    free: { color: 'slate', icon: <Star className="w-5 h-5" />, name: 'Free' },
    basic: { color: 'cyan', icon: <Zap className="w-5 h-5" />, name: 'Basic' },
    elite: { color: 'amber', icon: <Crown className="w-5 h-5" />, name: 'Elite' },
    promo: { color: 'emerald', icon: <Star className="w-5 h-5" />, name: 'Promo' },
    admin: { color: 'purple', icon: <Crown className="w-5 h-5" />, name: 'Admin' },
    trial: { color: 'teal', icon: <Clock className="w-5 h-5" />, name: 'Trial' },
  };

  const currentTierConfig = tierConfig[userData?.effectiveTier as keyof typeof tierConfig] || tierConfig.free;

  return (
    <div className="min-h-screen bg-terminal-bg">
      {/* Header */}
      <header className="sticky top-0 z-40 border-b border-terminal-border bg-terminal-bg/95 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="h-16 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <div className="relative">
                <div className="h-8 w-8 bg-gradient-to-br from-cyan-400 to-emerald-400 rounded flex items-center justify-center text-black font-black text-xs">
                  7S
                </div>
              </div>
              <span className="text-terminal-bright font-bold tracking-tight text-lg">
                ALPHA<span className="text-cyan-400">OPTIONS</span>
              </span>
            </Link>

            <div className="flex items-center gap-4">
              <Link
                to="/scanner"
                className="px-4 py-2 bg-gradient-to-r from-cyan-500 to-emerald-500 text-black rounded-lg font-bold text-sm hover:opacity-90"
              >
                Open Scanner
              </Link>

              <button
                onClick={() => signOut()}
                className="p-2 text-terminal-dim hover:text-white rounded-lg hover:bg-slate-800"
                title="Sign out"
              >
                <LogOut className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Section */}
        <div className="mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2">
            Welcome back, {user?.firstName || 'Trader'}!
          </h1>
          <p className="text-terminal-dim text-sm sm:text-base">Here's your trading overview</p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-6 sm:mb-8">
          {/* Subscription Tier */}
          <div className="bg-terminal-card rounded-xl border border-terminal-border p-4 sm:p-6">
            <div className="flex items-center justify-between mb-3 sm:mb-4">
              <span className="text-terminal-dim text-sm sm:text-base">Current Plan</span>
              <div className={`p-1.5 sm:p-2 rounded-lg bg-${currentTierConfig.color}-500/10 text-${currentTierConfig.color}-400`}>
                {currentTierConfig.icon}
              </div>
            </div>
            <div className="text-xl sm:text-2xl font-bold text-white">{currentTierConfig.name}</div>
            {userData?.hasValidPromo && (
              <div className="text-xs text-emerald-400 mt-1">
                Expires: {new Date(userData.promoExpiresAt!).toLocaleDateString()}
              </div>
            )}
          </div>

          {/* Scans Today */}
          <div className="bg-terminal-card rounded-xl border border-terminal-border p-4 sm:p-6">
            <div className="flex items-center justify-between mb-3 sm:mb-4">
              <span className="text-terminal-dim text-sm sm:text-base">Scans Today</span>
              <div className="p-1.5 sm:p-2 rounded-lg bg-cyan-500/10 text-cyan-400">
                <BarChart3 className="w-4 h-4 sm:w-5 sm:h-5" />
              </div>
            </div>
            <div className="text-lg sm:text-2xl font-bold text-white">
              {userData?.scansToday || 0} / {userData?.maxScans || 0}
            </div>
            <div className="mt-2 h-2 bg-slate-800 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-cyan-500 to-emerald-500 rounded-full transition-all"
                style={{ width: `${((userData?.scansToday || 0) / (userData?.maxScans || 1)) * 100}%` }}
              />
            </div>
          </div>

          {/* Remaining Scans */}
          <div className="bg-terminal-card rounded-xl border border-terminal-border p-4 sm:p-6">
            <div className="flex items-center justify-between mb-3 sm:mb-4">
              <span className="text-terminal-dim text-sm sm:text-base">Remaining</span>
              <div className="p-1.5 sm:p-2 rounded-lg bg-emerald-500/10 text-emerald-400">
                <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5" />
              </div>
            </div>
            <div className="text-xl sm:text-2xl font-bold text-white">{userData?.remainingScans || 0}</div>
            <div className="text-xs sm:text-sm text-terminal-dim mt-1">Resets at midnight</div>
          </div>

          {/* Watchlist Count */}
          <div className="bg-terminal-card rounded-xl border border-terminal-border p-4 sm:p-6">
            <div className="flex items-center justify-between mb-3 sm:mb-4">
              <span className="text-terminal-dim text-sm sm:text-base">Watchlist</span>
              <div className="p-1.5 sm:p-2 rounded-lg bg-amber-500/10 text-amber-400">
                <Star className="w-4 h-4 sm:w-5 sm:h-5" />
              </div>
            </div>
            <div className="text-xl sm:text-2xl font-bold text-white">{watchlist.length}</div>
            <div className="text-xs sm:text-sm text-terminal-dim mt-1">
              {userData?.effectiveTier === 'elite' ? '50 max' : '10 max'}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Watchlist */}
          <div className="lg:col-span-2">
            <div className="bg-terminal-card rounded-xl border border-terminal-border p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-bold text-white">Watchlist</h2>
                <span className="text-terminal-dim text-base">{watchlist.length} tickers</span>
              </div>

              {/* Add Ticker Form */}
              <form onSubmit={handleAddToWatchlist} className="flex gap-2 mb-6">
                <input
                  type="text"
                  value={newTicker}
                  onChange={(e) => setNewTicker(e.target.value.toUpperCase())}
                  placeholder="Add ticker (e.g., AAPL)"
                  className="flex-1 px-4 py-2 bg-slate-900 border border-terminal-border rounded-lg text-white placeholder-terminal-dim focus:border-cyan-500 focus:outline-none font-mono"
                  maxLength={10}
                />
                <button
                  type="submit"
                  className="p-3 sm:px-4 sm:py-2 bg-cyan-500/10 border border-cyan-500/30 text-cyan-400 rounded-lg hover:bg-cyan-500/20 transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
                >
                  <Plus className="w-5 h-5" />
                </button>
              </form>

              {/* Watchlist Items */}
              {loadingWatchlist ? (
                <div className="text-center py-8 text-terminal-dim">Loading watchlist...</div>
              ) : watchlist.length === 0 ? (
                <div className="text-center py-8 text-terminal-dim">
                  <Star className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>No tickers in watchlist</p>
                  <p className="text-sm">Add tickers above to track them</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {watchlist.map((item) => (
                    <div
                      key={item.id}
                      className="flex items-center justify-between p-3 bg-slate-900/50 rounded-lg hover:bg-slate-900 transition-colors group"
                    >
                      <div className="flex items-center gap-3">
                        <span className="font-mono font-bold text-white">{item.ticker}</span>
                        <span className="text-terminal-dim text-sm">
                          Added {new Date(item.added_at).toLocaleDateString()}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => navigate(`/scanner?ticker=${item.ticker}`)}
                          className="px-3 py-1 text-sm text-cyan-400 hover:bg-cyan-500/10 rounded transition-colors"
                        >
                          Scan
                        </button>
                        <button
                          onClick={() => handleRemoveFromWatchlist(item.ticker)}
                          className="p-1 text-slate-300 hover:text-rose-400 opacity-0 group-hover:opacity-100 transition-all"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Upgrade CTA (if not elite) */}
            {userData?.effectiveTier !== 'elite' && (
              <div className="bg-gradient-to-br from-amber-500/10 to-amber-600/10 rounded-xl border border-amber-500/30 p-6">
                <div className="flex items-center gap-3 mb-4">
                  <Crown className="w-6 h-6 text-amber-400" />
                  <h3 className="font-bold text-white">Upgrade to Elite</h3>
                </div>
                <ul className="space-y-2 text-sm text-terminal-dim mb-4">
                  <li className="flex items-center gap-2">
                    <ChevronRight className="w-4 h-4 text-amber-400" />
                    50 scans per day
                  </li>
                  <li className="flex items-center gap-2">
                    <ChevronRight className="w-4 h-4 text-amber-400" />
                    Elite trade management
                  </li>
                  <li className="flex items-center gap-2">
                    <ChevronRight className="w-4 h-4 text-amber-400" />
                    Priority support
                  </li>
                </ul>
                <Link
                  to="/pricing"
                  className="block w-full py-2 text-center bg-gradient-to-r from-amber-500 to-amber-600 text-black rounded-lg font-bold hover:opacity-90 transition-opacity"
                >
                  Upgrade Now
                </Link>
              </div>
            )}

            {/* Promo Code */}
            {!userData?.hasValidPromo && userData?.effectiveTier === 'free' && (
              <div className="bg-terminal-card rounded-xl border border-terminal-border p-6">
                <h3 className="font-bold text-white mb-4">Have a Promo Code?</h3>
                <form onSubmit={handlePromoSubmit} className="space-y-3">
                  <input
                    type="text"
                    value={promoCode}
                    onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                    placeholder="Enter code"
                    className="w-full px-4 py-2 bg-slate-900 border border-terminal-border rounded-lg text-white placeholder-terminal-dim focus:border-cyan-500 focus:outline-none font-mono text-sm"
                  />
                  <button
                    type="submit"
                    className="w-full py-2 bg-slate-800 border border-terminal-border text-white rounded-lg font-medium hover:bg-slate-700 transition-colors text-sm"
                  >
                    Apply Code
                  </button>
                </form>
                {promoMessage && (
                  <div className={`mt-3 p-2 rounded text-sm text-center ${
                    promoMessage.type === 'success'
                      ? 'bg-emerald-500/10 text-emerald-400'
                      : 'bg-rose-500/10 text-rose-400'
                  }`}>
                    {promoMessage.text}
                  </div>
                )}
              </div>
            )}

            {/* Account Settings */}
            <div className="bg-terminal-card rounded-xl border border-terminal-border p-6">
              <h3 className="font-bold text-white mb-4">Account</h3>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-terminal-dim">Email</span>
                  <span className="text-white">{user?.primaryEmailAddress?.emailAddress}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-terminal-dim">Member since</span>
                  <span className="text-white">
                    {user?.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'N/A'}
                  </span>
                </div>
              </div>

              {userData?.subscriptionStatus === 'active' && !userData?.hasValidPromo && (
                <button
                  onClick={handleCancelSubscription}
                  className="w-full mt-4 py-2 text-rose-400 text-sm hover:bg-rose-500/10 rounded-lg transition-colors"
                >
                  Cancel Subscription
                </button>
              )}
            </div>

            {/* Quick Actions */}
            <div className="bg-terminal-card rounded-xl border border-terminal-border p-6">
              <h3 className="font-bold text-white mb-4">Quick Actions</h3>
              <div className="space-y-2">
                <Link
                  to="/scanner"
                  className="flex items-center justify-between p-3 bg-slate-900/50 rounded-lg hover:bg-slate-900 transition-colors"
                >
                  <span className="text-terminal-text">Open Scanner</span>
                  <ChevronRight className="w-4 h-4 text-terminal-dim" />
                </Link>
                <Link
                  to="/positions"
                  className="flex items-center justify-between p-3 bg-gradient-to-r from-cyan-500/10 to-emerald-500/10 border border-cyan-500/30 rounded-lg hover:border-cyan-500/50 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <Target className="w-4 h-4 text-cyan-400" />
                    <span className="text-terminal-text">My Positions</span>
                    <span className="text-xs bg-gradient-to-r from-cyan-500/30 to-emerald-500/30 text-cyan-300 px-1.5 py-0.5 rounded">NEW</span>
                  </div>
                  <ChevronRight className="w-4 h-4 text-cyan-400" />
                </Link>
                <Link
                  to="/research"
                  className="flex items-center justify-between p-3 bg-gradient-to-r from-emerald-500/10 to-teal-500/10 border border-emerald-500/30 rounded-lg hover:border-emerald-500/50 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <Search className="w-4 h-4 text-emerald-400" />
                    <span className="text-terminal-text">Research Engine</span>
                    <span className="text-xs bg-gradient-to-r from-emerald-500/30 to-teal-500/30 text-emerald-300 px-1.5 py-0.5 rounded">BETA</span>
                  </div>
                  <ChevronRight className="w-4 h-4 text-emerald-400" />
                </Link>
                <Link
                  to="/moonshot"
                  className="flex items-center justify-between p-3 bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/30 rounded-lg hover:border-purple-500/50 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <Rocket className="w-4 h-4 text-purple-400" />
                    <span className="text-terminal-text">Ask Moonshot</span>
                    <span className="text-xs bg-gradient-to-r from-purple-500/30 to-pink-500/30 text-purple-300 px-1.5 py-0.5 rounded">AI</span>
                  </div>
                  <ChevronRight className="w-4 h-4 text-purple-400" />
                </Link>
                <Link
                  to="/pricing"
                  className="flex items-center justify-between p-3 bg-slate-900/50 rounded-lg hover:bg-slate-900 transition-colors"
                >
                  <span className="text-terminal-text">View Plans</span>
                  <ChevronRight className="w-4 h-4 text-terminal-dim" />
                </Link>
                <Link
                  to="/guide"
                  className="flex items-center justify-between p-3 bg-slate-900/50 rounded-lg hover:bg-slate-900 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <BookOpen className="w-4 h-4 text-cyan-400" />
                    <span className="text-terminal-text">How to Use</span>
                  </div>
                  <ChevronRight className="w-4 h-4 text-terminal-dim" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default DashboardPage;
